import { Info } from '@mui/icons-material';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'src/components/molecules/page-header';
import { useDialog } from 'src/hooks/use-dialog';

import { useOrganizationDetails } from '../../hooks/query/use-organization-details';
import { AddOrganizationLogoDialog } from '../organisms/logo/dialogs/add-organization-logo-dialog';
import RemoveOrganizationLogoDialog from '../organisms/logo/dialogs/remove-organization-logo-dialog';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { Can, withPermissions } from '@/permissions/can';

const Logo = () => {
  const { t } = useTranslation(['settings', 'common']);

  const { data, error, isLoading } = useOrganizationDetails();

  const [
    isOpenAddOrganizationLogoDialog,
    handleOpenAddOrganizationLogoDialog,
    handleCloseAddOrganizationLogoDialog,
  ] = useDialog();

  const [isOpenRemoveLogoDialog, handleOpenRemoveLogoDialog, handleCloseRemoveLogoDialog] =
    useDialog();

  const renderLogoContent = () => {
    if (isLoading) return;
    if (data?.logo) {
      return (
        <Box
          component="img"
          sx={{
            maxWidth: '300px',
            height: 'auto',
            p: 2,
          }}
          alt="The house from the offer."
          src={data?.logo}
        />
      );
    } else {
      return (
        <Box
          sx={{
            mb: 4,
            p: 2,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'primary.main',
            borderRadius: '5px',
            width: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={handleOpenAddOrganizationLogoDialog}>
          <Typography>{t('settings:logo.noLogo')}</Typography>
        </Box>
      );
    }
  };

  return (
    <>
      <PageHeader title="" />
      <Typography variant="h3">{t('settings:logo.title')}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
        <Info />
        <Typography>{t('settings:logo.description')}</Typography>
      </Box>

      <Grid
        sx={{
          gap: 2,
        }}>
        {renderLogoContent()}
        <DataStatusIndicator
          dataLength={data ? 1 : 0}
          error={error?.response?.data?.message}
          isFetching={isLoading}
        />

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mt: 2,
          }}>
          <Can I="create" a="OrganizationLogo" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    onClick={handleOpenAddOrganizationLogoDialog}
                    variant="contained"
                    disabled={!!data?.logo || !allowed}>
                    {t('settings:logo.addLogo')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
          <Can I="delete" a="OrganizationLogo" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    onClick={handleOpenRemoveLogoDialog}
                    variant="outlined"
                    disabled={!data?.logo || !allowed}>
                    {t('settings:logo.removeLogo')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        </Box>
      </Grid>
      <AddOrganizationLogoDialog
        open={isOpenAddOrganizationLogoDialog}
        onCancel={handleCloseAddOrganizationLogoDialog}
      />
      <RemoveOrganizationLogoDialog
        open={isOpenRemoveLogoDialog}
        onCancel={handleCloseRemoveLogoDialog}
      />
    </>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'OrganizationLogo'), Logo);
